import React, { useState, useEffect, useCallback } from "react";
import Dashboard from "components/dashboard";
import { PageHeaderBar } from "components/page";
import { QueryResponseHandler } from "components/graphql";
import { handleSubmit } from "./forms/edit-form";
import ValidatedForm from "components/form";
import { useAuthDataContext } from "components/auth";
import { RestrictedEventVisible, RestrictedEventNotDeleted, handleEventSubmitError } from "pages/event/utils";
import {
  EventForm,
  getDefaultValuesByStatus,
  getValuesFromEventToEdit,
  getValidationSchemaByStatus,
} from "./forms/edit-form";
import { EventFormProvider, useEventFormContext } from "pages/event/forms/utils";
import PropTypes from "prop-types";
import { storeTiiviiOttConfigActionCreator } from "../../actions/common-actions";
import { useDispatch } from "react-redux";
import { getAuthorizedRecording } from "../../services/eventServices";
import { getContractHasTiiviiOtt } from "../../services/commonService";
import { useEventDetailsRest } from "./details-page/rest-event-adapter";

const EditForm = ({ intl, history, eventToEdit, contract, deployment }) => {
  const dispatch = useDispatch();
  const { showSportDataConfig } = useEventFormContext();
  const { hasAdminPrivileges } = useAuthDataContext();
  const [isSportEvent, setIsSportEvent] = useState(false);
  const [isLocalRec, setIsLocalRec] = useState(false);
  const [showLocalRecEventFields, setShowLocalRecEventFields] = useState(false);
  const [eventStatus, setEventStatus] = useState(null);
  const [initialValues, setInitialValues] = useState(
    getDefaultValuesByStatus({ eventStatus, hasAdminPrivileges, isSportEvent }),
  );
  const [isAuthorizedRecording, setAuthorizedRecording] = useState(false);
  const [hasTiiviiOttConfig, setHasTiiviiOttConfig] = useState(false);

  // No se usa useCurrentCustomerContext().contracts.find(...) a propósito: esa lista son solo
  // los contratos propios del usuario logueado, y un admin editando el evento de otro cliente
  // podría no tenerlo ahí. `contract`/`deployment` ya llegan resueltos por id desde el hook.
  const customer = contract
    ? {
        contract: {
          id: contract._id,
          customerId: contract.customer_id,
          hasRoomTemplates: eventToEdit.contract?.hasRoomTemplates ?? false,
          localRecAvailable: contract.local_rec_available || false,
          recordAndShareAvailable: contract.record_and_share_available || false,
          allowQod: deployment?.allow_qod || false,
          msisdnQod: deployment?.msisdn_qod || "",
          tiiviiOttConfig: {
            enabled: contract.tiivii_ott_config?.enabled || false,
            webhookUrl: contract.tiivii_ott_config?.webhook_url || "",
            nimbleConfigUrl: contract.tiivii_ott_config?.nimble_config_url || "",
          },
        },
      }
    : null;

  const storeTiiviiOttConfig = data => {
    dispatch(storeTiiviiOttConfigActionCreator(data));
  };

  const hasRoomTemplates = eventToEdit && eventToEdit.contract ? eventToEdit.contract.hasRoomTemplates : false;
  const sportEvent = eventToEdit && eventToEdit.type && eventToEdit.type.isSportEvent;

  const handleContractHasTiiviiOttConfig = data => {
    setHasTiiviiOttConfig(data.has_tiivii_ott_configured);
  };

  const handleServiceHasTiiviiOttConfig = useCallback(
    () => {
      getContractHasTiiviiOtt({ contractId: eventToEdit?.contractId }, handleContractHasTiiviiOttConfig);
    },
    [eventToEdit.contractId],
  );

  const handleSetAuthorizedRecording = response => setAuthorizedRecording(response.record_and_share_available);
  useEffect(
    () => {
      if (eventToEdit?.contractId) {
        handleServiceHasTiiviiOttConfig();
      }
    },
    [eventToEdit.contractId, handleServiceHasTiiviiOttConfig],
  );

  useEffect(
    () => {
      if (hasTiiviiOttConfig) {
        storeTiiviiOttConfig(hasTiiviiOttConfig);
      }
      return () => storeTiiviiOttConfig(null);
    },
    [hasTiiviiOttConfig],
  );

  useEffect(
    () => {
      if (eventToEdit) {
        getAuthorizedRecording(eventToEdit?.contractId, handleSetAuthorizedRecording);
        setIsSportEvent(sportEvent);
        setInitialValues(
          getValuesFromEventToEdit({
            eventToEdit,
            hasAdminPrivileges,
            isSportEvent: sportEvent,
            canEditEventType: true,
            canEditRoomTemplate: true,
          }),
        );
        setEventStatus(eventToEdit.eventStatus);
      }
    },
    [eventToEdit, hasAdminPrivileges, sportEvent],
  );

  return (
    <ValidatedForm
      initialValues={initialValues}
      validationSchema={getValidationSchemaByStatus({
        eventStatus,
        intl,
        startDate: initialValues.startDate,
        hasAdminPrivileges,
        hasRoomTemplates,
        isSportEvent,
        showSportDataConfig,
        isLocalRec,
        showLocalRecEventFields,
      })}
      handleLoadingOverlay
      onSubmit={handleSubmit({ history, eventToEdit })}
      onSubmitError={handleEventSubmitError(intl)}
    >
      {props => (
        <EventForm
          customer={customer}
          eventToEdit={eventToEdit}
          eventStatus={eventStatus}
          isAuthorizedRecording={isAuthorizedRecording}
          isSportEvent={isSportEvent}
          setIsSportEvent={setIsSportEvent}
          isLocalRec={isLocalRec}
          setIsLocalRec={setIsLocalRec}
          showLocalRecEventFields={showLocalRecEventFields}
          setShowLocalRecEventFields={setShowLocalRecEventFields}
          hasAdminPrivileges={hasAdminPrivileges}
          eventCustomerId={eventToEdit.customerId}
          showTestTemplates={eventToEdit.roomTemplate && eventToEdit.roomTemplate.test}
          {...props}
        />
      )}
    </ValidatedForm>
  );
};

EditForm.propTypes = {
  /**
   * Evento a editar
   */
  eventToEdit: PropTypes.object,
  /**
   * Contrato del evento, ya resuelto por useEventDetailsRest
   */
  contract: PropTypes.object,
  /**
   * Instalación del contrato del evento, ya resuelta por useEventDetailsRest
   */
  deployment: PropTypes.object,
};

const EventEditPage = ({
  intl,
  history,
  match: {
    params: { id },
  },
}) => {
  const { event: eventToEdit, loading, error, contract, deployment } = useEventDetailsRest({ eventId: id });

  return (
    <Dashboard>
      <QueryResponseHandler error={error} loading={loading} elements={eventToEdit}>
        <EventFormProvider>
          <RestrictedEventNotDeleted event={eventToEdit}>
            <RestrictedEventVisible event={eventToEdit}>
              <PageHeaderBar
                showBackButton
                title="app.page.event.edit"
                titleParams={{ eventTitle: eventToEdit ? eventToEdit.title : "" }}
              />
              <EditForm
                intl={intl}
                history={history}
                eventToEdit={eventToEdit}
                contract={contract}
                deployment={deployment}
              />
            </RestrictedEventVisible>
          </RestrictedEventNotDeleted>
        </EventFormProvider>
      </QueryResponseHandler>
    </Dashboard>
  );
};

export default EventEditPage;
